<template>
  <div class="expense-report-beprince">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Expense Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalRecords }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading expense report...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <p v-if="campusAddress" class="campus-address mb-0 mt-50">
              {{ campusAddress }}
            </p>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Expense Report</span>
              <span class="date-range-text">
                <strong
                  >{{ formatHeaderDate(dtFrom) }} to
                  {{ formatHeaderDate(dtTo) }}</strong
                >
              </span>
            </div>
            <div v-if="contactLine" class="contact-line mt-50">
              {{ contactLine }}
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="summary-section mb-4">
        <div class="summary-card">
          <span class="label">Records</span>
          <span class="value">{{ totalRecords }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Master Heads</span>
          <span class="value">{{ groups.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Grand Total</span>
          <span class="value">{{ formatAmount(grandTotal) }}</span>
        </div>
      </div>

      <div v-if="!groups.length" class="empty-state">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No expense entries found for this period.</p>
      </div>

      <section
        v-for="(master, mIndex) in groups"
        :key="'master-' + (master.masterHeadID || mIndex)"
        class="report-section"
      >
        <div class="section-heading">
          <span class="section-title">{{
            cleanTitle(master.masterHeadTitle || reportTitle || "Expenses")
          }}</span>
          <span class="section-total">{{
            formatAmount(master.masterTotal)
          }}</span>
        </div>

        <div
          v-for="(account, aIndex) in master.accounts || []"
          :key="'acc-' + (account.accountID || aIndex)"
          class="account-block"
        >
          <div
            v-if="showAccountSubHeader(master, account)"
            class="account-subhead"
          >
            <span>{{ cleanTitle(account.accountTitle || "Other") }}</span>
            <span class="master-tag"
              >Master Head:
              {{
                cleanTitle(master.masterHeadTitle || reportTitle || "Expenses")
              }}</span
            >
          </div>

          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center col-date">DATE</th>
                  <th class="text-center col-voucher">VOUCHER #</th>
                  <th class="text-left col-desc">DESCRIPTION</th>
                  <th class="text-right col-amount">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(entry, eIndex) in account.entries || []"
                  :key="
                    'entry-' + (entry.detailID || entry.expenseID || eIndex)
                  "
                >
                  <td class="text-center">{{ formatTableDate(entry.date) }}</td>
                  <td class="text-center voucher-cell">
                    {{ formatVoucher(resolveVoucher(entry)) }}
                  </td>
                  <td class="text-left desc-cell">
                    {{ cleanTitle(entry.description || "-") }}
                  </td>
                  <td class="text-right amount-cell font-weight-bold">
                    {{ formatAmount(entry.amount) }}
                  </td>
                </tr>
                <tr v-if="!(account.entries || []).length" class="no-data-row">
                  <td colspan="4" class="text-center text-muted">No entries</td>
                </tr>
                <tr class="total-row">
                  <td colspan="3" class="text-right font-weight-bold">
                    Total — {{ cleanTitle(account.accountTitle || "Other") }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatAmount(account.accountTotal) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="showMasterTotal(master)" class="master-total-bar">
          <span
            >Total —
            {{
              cleanTitle(master.masterHeadTitle || reportTitle || "Expenses")
            }}</span
          >
          <span>{{ formatAmount(master.masterTotal) }}</span>
        </div>
      </section>

      <div v-if="groups.length" class="grand-total-bar">
        <span>Grand Total</span>
        <span>{{ formatAmount(grandTotal) }}</span>
      </div>

      <div class="page-footer">Generated on {{ currentDateTime }}</div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ExpenseReportBeprincePrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      groups: [],
      reportTitle: "",
      grandTotal: 0,
      campusName: "MY SKOOL",
      campusBranch: "",
      campusAddress: "",
      schoolLogo: null,
      phone: "",
      email: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
      accountID: 0,
      period: "",
    };
  },
  computed: {
    contactLine() {
      const parts = [];
      if (this.phone) parts.push(`Phone: ${this.phone}`);
      if (this.email) parts.push(`Email: ${this.email}`);
      return parts.join("  ·  ");
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totalRecords() {
      return this.groups.reduce((sum, master) => {
        return (
          sum +
          (master.accounts || []).reduce(
            (accSum, account) => accSum + ((account.entries || []).length || 0),
            0
          )
        );
      }, 0);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    cleanTitle(value) {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ");
    },
    showAccountSubHeader(master, account) {
      const accounts = master.accounts || [];
      if (accounts.length > 1) return true;
      const masterTitle = this.cleanTitle(master.masterHeadTitle).toUpperCase();
      const accountTitle = this.cleanTitle(account.accountTitle).toUpperCase();
      return masterTitle && accountTitle && masterTitle !== accountTitle;
    },
    showMasterTotal(master) {
      const accounts = master.accounts || [];
      return accounts.length > 1;
    },
    applyBranchFallback() {
      const branches = this.$store.state.branches || [];
      const currentBranch =
        branches.find((b) => b.id == this.cID) ||
        this.$store.state.currentBranch ||
        {};
      this.campusName =
        currentBranch.organizationName ||
        currentBranch.name ||
        currentBranch.branch ||
        "MY SKOOL";
      this.campusBranch = currentBranch.branch || "";
      this.campusAddress =
        currentBranch.address ||
        currentBranch.fullAddress ||
        currentBranch.location ||
        "";
      this.schoolLogo = currentBranch.logo || null;
      this.phone =
        currentBranch.phone ||
        currentBranch.contact ||
        currentBranch.mobile ||
        "";
      this.email = currentBranch.email || "";
    },
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || query.cId || 0;
      this.accountID =
        query.accountID != null ? Number(query.accountID) || 0 : 0;
      this.dtFrom = query.dtFrom || "";
      this.dtTo = query.dtTo || "";
      this.period = query.period || "";

      this.applyBranchFallback();

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name || this.campusName;
            this.campusBranch =
              this.cID == 0 ? "All Campuses" : match.branch || "";
            this.schoolLogo = match.logo || this.schoolLogo;
            this.campusAddress =
              match.address ||
              match.fullAddress ||
              match.location ||
              this.campusAddress;
            this.phone =
              match.phone || match.contact || match.mobile || this.phone || "";
            this.email = match.email || this.email || "";
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        this.applyBranchFallback();
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) {
          baseUrl += "/";
        }

        const params = {
          db: this.db,
          cID: this.cID,
          accountID: this.accountID || 0,
        };
        if (this.dtFrom) params.dtFrom = this.dtFrom;
        if (this.dtTo) params.dtTo = this.dtTo;
        if (this.period) params.period = this.period;

        const response = await axios.get(
          `${baseUrl}Expenses/GetExpenseReport`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data;
        if (!payload || payload.status !== "success") {
          this.error =
            (payload && payload.message) ||
            "Failed to load expense report data.";
          this.groups = [];
          return;
        }

        const data = payload.data || {};
        this.groups = Array.isArray(data.groups) ? data.groups : [];
        this.reportTitle = data.reportTitle || "";
        this.grandTotal = Number(data.grandTotal) || 0;
        if (data.dtFrom) this.dtFrom = data.dtFrom;
        if (data.dtTo) this.dtTo = data.dtTo;
      } catch (error) {
        console.error("Failed to load expense report:", error);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },
    resolveVoucher(entry) {
      if (!entry) return "";
      return (
        entry.voucherNumStr ||
        entry.voucherNum ||
        entry.voucherID ||
        entry.expenseID
      );
    },
    formatAmount(value) {
      const num = Number(value) || 0;
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    formatVoucher(value) {
      if (value === undefined || value === null || value === "") return "-";
      const raw = String(value).trim();
      if (!raw) return "-";
      const num = Number(raw.replace(/,/g, ""));
      if (!Number.isNaN(num) && raw !== "") {
        return new Intl.NumberFormat("en-PK").format(num);
      }
      return raw;
    },
    formatHeaderDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = date
          .toLocaleString("en-GB", { month: "short" })
          .toUpperCase();
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return dateString;
      }
    },
    formatTableDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-GB", { month: "short" });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch (e) {
        return dateString;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.expense-report-beprince {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .campus-address,
  .contact-line {
    font-size: 0.95rem;
    color: #555;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
      color: #2d3436;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;

  p {
    margin: 0;
    font-weight: 600;
  }
}

.report-section {
  margin-bottom: 1.75rem;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #2c3e50;
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
}

.section-title {
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 0.95rem;
}

.section-total {
  font-weight: 800;
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
}

.account-block {
  margin-bottom: 0.75rem;
}

.account-subhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  border-bottom: none;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  color: #2d3436;

  .master-tag {
    color: #636e72;
    font-weight: 600;
    text-transform: none;
  }
}

.table-container {
  width: 100%;
  overflow: visible;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
  background: white;
  margin: 0;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 12px;
    box-sizing: border-box;
    font-size: 0.92rem;
    vertical-align: middle;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even):not(.total-row):not(.no-data-row) {
    background-color: #f8f9fa;
  }

  .voucher-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .desc-cell {
    text-transform: uppercase;
  }

  .amount-cell {
    font-variant-numeric: tabular-nums;
  }

  .total-row {
    background: #f1f3f5 !important;

    td {
      font-weight: 800;
      border-top: 2px solid #2c3e50;
      color: #2c3e50;
    }
  }
}

.col-date {
  width: 16%;
}

.col-voucher {
  width: 14%;
}

.col-desc {
  width: 52%;
}

.col-amount {
  width: 18%;
}

.master-total-bar,
.grand-total-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.9rem;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  color: #2c3e50;
  margin-top: 6px;
}

.grand-total-bar {
  background: #2c3e50;
  color: #fff;
  border-color: #2c3e50;
  border-radius: 8px;
  margin-top: 1.25rem;
  font-size: 1rem;
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

.text-center {
  text-align: center;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.font-weight-bold {
  font-weight: 700;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .expense-report-beprince {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-header-modern {
    margin-bottom: 18px !important;
    padding-bottom: 12px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 90px !important;
      max-width: 220px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.45rem !important;
    }

    .campus-address,
    .contact-line,
    .report-info-line {
      font-size: 0.85rem !important;
    }
  }

  .summary-section {
    margin: 8px 0 14px !important;
    padding: 10px !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;
    background: #fff !important;

    .summary-card {
      .label {
        font-size: 0.7rem !important;
        color: #000 !important;
      }
      .value {
        font-size: 1rem !important;
        color: #000 !important;
      }
    }
  }

  .report-section {
    page-break-inside: auto !important;
    break-inside: auto !important;
    margin-bottom: 12px !important;
  }

  .section-heading {
    background: #f1f2f6 !important;
    color: #000 !important;
    border: 1px solid #000 !important;
    border-bottom: none !important;
    border-radius: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    page-break-after: avoid !important;
  }

  .account-subhead {
    background: #fff !important;
    border: 1px solid #000 !important;
    border-bottom: none !important;
    color: #000 !important;
    page-break-after: avoid !important;

    .master-tag {
      color: #000 !important;
    }
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;
    page-break-inside: auto !important;

    th,
    td {
      padding: 5px 8px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead {
      display: table-header-group !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr {
      page-break-inside: avoid !important;
    }

    .voucher-cell {
      color: #000 !important;
    }

    .total-row {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;

      td {
        border-top: 2px solid #000 !important;
        color: #000 !important;
      }
    }
  }

  .master-total-bar,
  .grand-total-bar {
    background: #fff !important;
    color: #000 !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    page-break-inside: avoid !important;
  }

  .grand-total-bar {
    border: 2px solid #000 !important;
  }

  .page-footer {
    color: #000 !important;
    border-top: 1px solid #000 !important;
  }
}
</style>
